import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { publicFileUrl } from "@/lib/utils-app";
import AppShell from "@/components/AppShell";
import PullToRefresh from "@/components/PullToRefresh";
import { CardGridSkeleton } from "@/components/SkeletonList";
import { Input } from "@/components/ui/input";
import StarRating from "@/components/StarRating";
import { ShieldCheckIcon as ShieldCheck, GraduationCapIcon as GraduationCap, ScissorsIcon as Scissors, FootprintsIcon as Footprints, HouseIcon as House, MapPinIcon as MapPin, SparkleIcon as Sparkle, TreeIcon as Tree } from "@phosphor-icons/react";

/**
 * `/find-pros` — public discovery for independent trainers / groomers
 * / walkers / sitters. Backed by GET /api/public/providers/discover
 * (only approved providers returned, so no client-side status filter
 * needed).
 *
 * We deliberately named this route `/find-pros` rather than
 * `/services` because `/services` is already the platform's
 * paid-plans marketing page.
 */
const KIND_META = {
  trainer: { label: "Trainers", Icon: GraduationCap, accent: "#0E0E0E" },
  groomer: { label: "Groomers", Icon: Scissors,     accent: "#C9A227" },
  walker:  { label: "Walkers",  Icon: Footprints,   accent: "#0F8043" },
  sitter:  { label: "Sitters",  Icon: House,        accent: "#C1272D" },
  field_host: { label: "Zoomie Time", Icon: Tree, accent: "#2E6B3E" },
  other:   { label: "Other",    Icon: Sparkle,      accent: "#3A6EA5" },
};

export default function FindPros() {
  const [params, setParams] = useSearchParams();
  const [kind, setKind] = useState(params.get("kind") || "");
  const [zip, setZip] = useState(params.get("zip") || "");
  const [q, setQ] = useState(params.get("q") || "");
  const [insVerified, setInsVerified] = useState(params.get("insurance_verified") === "1");
  const [bgClear, setBgClear] = useState(params.get("bg_clear") === "1");
  const [weekday, setWeekday] = useState(params.get("available_weekday") || "");
  const [priceMax, setPriceMax] = useState(params.get("price_max") || "");
  const [fencedOnly, setFencedOnly] = useState(false);
  const [minAcres, setMinAcres] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState({});

  // Client-side land filters (only meaningful when browsing Private Fields).
  const displayRows = React.useMemo(() => {
    if (kind !== "field_host" || (!fencedOnly && !minAcres)) return rows;
    return rows.filter((r) => {
      const land = r.land || {};
      if (fencedOnly && land.fenced !== "fully") return false;
      if (minAcres && Number(land.acres || 0) < Number(minAcres)) return false;
      return true;
    });
  }, [rows, kind, fencedOnly, minAcres]);

  const fetchPros = React.useCallback(async () => {
    const p = {};
    if (kind) p.kind = kind;
    if (zip) p.zip = zip;
    if (q) p.q = q;
    if (insVerified) p.insurance_verified = "true";
    if (bgClear) p.bg_clear = "true";
    if (weekday !== "" && weekday != null) p.available_weekday = weekday;
    if (priceMax) p.price_max = priceMax;
    try {
      const r = await api.get("/public/providers/discover", { params: p });
      setRows(r.data || []);
      if (localStorage.getItem("token") && (r.data || []).length) {
        api.post("/providers/match-scores", { provider_ids: r.data.slice(0, 60).map((x) => x.id) })
          .then((m) => setMatches(m.data || {}))
          .catch(() => {});
      }
    } catch {
      setRows([]);
    }
  }, [kind, zip, q, insVerified, bgClear, weekday, priceMax]);

  useEffect(() => {
    setLoading(true);
    fetchPros().finally(() => setLoading(false));
    const next = new URLSearchParams();
    if (kind) next.set("kind", kind);
    if (zip) next.set("zip", zip);
    if (q) next.set("q", q);
    if (insVerified) next.set("insurance_verified", "1");
    if (bgClear) next.set("bg_clear", "1");
    if (weekday !== "" && weekday != null) next.set("available_weekday", String(weekday));
    if (priceMax) next.set("price_max", priceMax);
    setParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, zip, q, insVerified, bgClear, weekday, priceMax]);

  return (
    <AppShell>
      <PullToRefresh onRefresh={fetchPros} testId="find-pros-ptr">
      <div className="max-w-6xl">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">Independent providers</p>
        <h1 className="font-spectral font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          Trainers · Groomers · Walkers · Sitters &amp; More
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          Hire an independent, vetted provider near you. Each one runs their own business — Armstrong K9 Registry is the venue connecting you. <strong className="text-ink">They set their prices; you set the schedule.</strong>
        </p>

        <div className="gold-hairline my-6 max-w-md" />

        <div className="flex flex-wrap gap-3 mb-3">
          <div className="flex flex-wrap gap-1">
            <FilterChip active={!kind} onClick={() => setKind("")} testid="filter-all">All</FilterChip>
            {Object.entries(KIND_META).map(([k, m]) => (
              <FilterChip key={k} active={kind === k} onClick={() => setKind(kind === k ? "" : k)} testid={`filter-${k}`}>
                <m.Icon size={12} weight="duotone" className="mr-1" />
                {m.label}
              </FilterChip>
            ))}
          </div>
          <div className="flex gap-2 sm:ml-auto">
            <Input
              placeholder="ZIP"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              className="w-24"
              data-testid="filter-zip"
            />
            <Input
              placeholder="Search by name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-56"
              data-testid="filter-q"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="filter-row-2">
          <FilterChip active={insVerified} onClick={() => setInsVerified(!insVerified)} testid="filter-insurance-verified">
            <ShieldCheck size={12} weight="duotone" className="mr-1" /> Insurance verified
          </FilterChip>
          <FilterChip active={bgClear} onClick={() => setBgClear(!bgClear)} testid="filter-bg-clear">
            <ShieldCheck size={12} weight="duotone" className="mr-1" /> Background check cleared
          </FilterChip>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Open</span>
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
              <button
                key={i}
                onClick={() => setWeekday(weekday === String(i) ? "" : String(i))}
                data-testid={`filter-weekday-${i}`}
                className={`text-[11px] font-semibold rounded-full w-7 h-7 transition-colors ${
                  weekday === String(i) ? "bg-ink text-white" : "bg-white border border-[#EAEAEA] text-ink hover:border-gold/55"
                }`}
              >
                {d[0]}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Max $</span>
            <Input
              type="number"
              min="0"
              placeholder="—"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              className="w-20 h-8"
              data-testid="filter-price-max"
            />
          </div>
          {kind === "field_host" && (
            <>
              <FilterChip active={fencedOnly} onClick={() => setFencedOnly(!fencedOnly)} testid="filter-fully-fenced">
                <Tree size={12} weight="duotone" className="mr-1" /> Fully fenced
              </FilterChip>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Min acres</span>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="—"
                  value={minAcres}
                  onChange={(e) => setMinAcres(e.target.value)}
                  className="w-20 h-8"
                  data-testid="filter-min-acres"
                />
              </div>
            </>
          )}
          {(insVerified || bgClear || weekday !== "" || priceMax) && (
            <button
              type="button"
              onClick={() => { setInsVerified(false); setBgClear(false); setWeekday(""); setPriceMax(""); }}
              className="text-[11px] text-crimson hover:underline ml-auto"
              data-testid="filter-clear"
            >
              Clear filters
            </button>
          )}
        </div>
        <p className="text-[11px] text-[#6B6B6B] mb-6 max-w-2xl leading-relaxed" data-testid="bg-clear-helper">
          <strong className="text-ink">Background check cleared</strong> means the provider passed an independent FCRA-compliant criminal-record screen run by a third-party vendor. <strong className="text-ink">Insurance verified</strong> means Armstrong K9 Registry has confirmed an active certificate of insurance on file. Strongly recommended for walkers + sitters where the dog leaves the owner's sight.
        </p>

        {!loading && displayRows.length > 0 && (
          <div className="flex items-center justify-between mb-4 text-[12px] text-[#6B6B6B]" data-testid="results-summary">
            <span>
              Showing <strong className="text-ink">{displayRows.length}</strong>{" "}
              {displayRows.length === 1 ? "provider" : "providers"}
              {(() => {
                const cleared = displayRows.filter((r) => r.background_check_status === "clear").length;
                if (cleared === 0) return null;
                return (
                  <>
                    {" · "}
                    <span className="text-[#0F8043] font-semibold inline-flex items-center gap-1">
                      <ShieldCheck size={11} weight="fill" />
                      {cleared} background-check cleared
                    </span>
                  </>
                );
              })()}
            </span>
            <span className="text-[10.5px] uppercase tracking-wider text-[#6B6B6B]">
              Sort: most trusted first
            </span>
          </div>
        )}

        {loading ? (
          <CardGridSkeleton count={6} withImage={false} testId="find-pros-skeleton" />
        ) : displayRows.length === 0 ? (
          <div className="bg-white border border-[#EAEAEA] rounded-xl p-8 text-center" data-testid="discover-empty">
            <p className="font-semibold text-ink">No providers match those filters yet.</p>
            <p className="text-xs text-[#6B6B6B] mt-1">
              We're in local launch — try removing the ZIP filter or check back soon.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayRows.map((p) => (
              <ProviderCard key={p.id} p={p} match={matches[p.id]} />
            ))}
          </div>
        )}

        {/* Recruiting callout — unique pros can join under the new
            "Other" category with their own specialty label. */}
        <div
          className="mt-10 bg-ink text-white rounded-2xl p-6 md:p-8 relative overflow-hidden"
          data-testid="other-category-callout"
        >
          <div aria-hidden className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-gold/15 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2 flex items-center gap-1.5">
                <Sparkle size={12} weight="fill" /> New categories welcome
              </p>
              <h2 className="font-spectral text-lg leading-snug">
                Run a pet business — or own a great piece of land?
              </h2>
              <p className="text-[13px] text-white/70 mt-1.5 leading-relaxed max-w-xl">
                Pooper scoopers, pet photographers, canine massage, pet taxis — list under your
                own specialty. Got a field? Host <strong className="text-white">Zoomie Time</strong>:
                rent your private land by the hour for safe, off-leash zoomies. Set your services
                and prices, get booked and paid right here.
              </p>
            </div>
            <Link
              to="/provider/me"
              data-testid="other-category-callout-cta"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-gold-gradient text-ink text-[13px] font-bold px-6 h-11 hover:opacity-90 transition-opacity flex-shrink-0"
            >
              List your business →
            </Link>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-[#EAEAEA] text-[12px] text-[#6B6B6B] max-w-2xl leading-relaxed">
          <strong className="text-ink">Venue disclosure:</strong> Armstrong K9 Registry connects customers with independent service providers and is not a party to any service transaction. Providers — not Armstrong K9 Registry — are responsible for performing their services and for carrying whatever insurance and licensing their jurisdiction or business requires. Insurance, licensing, and background-check details shown on a provider profile are self-reported by the provider; insurance and background-check verification are strongly recommended for trades where the dog leaves the owner&rsquo;s sight (walking, sitting, boarding) but are not required by Armstrong K9 Registry to list.
        </div>
      </div>
      </PullToRefresh>
    </AppShell>
  );
}

function FilterChip({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center text-[12px] font-semibold rounded-full px-3 h-9 transition-colors ${
        active
          ? "bg-ink text-white"
          : "bg-white border border-[#EAEAEA] text-ink hover:border-gold/55 hover:bg-[#FAFAFA]"
      }`}
    >
      {children}
    </button>
  );
}

function ProviderCard({ p, match }) {
  const meta = KIND_META[p.kind] || { label: p.kind, Icon: GraduationCap };
  const Icon = meta.Icon;
  const bgCleared = p.background_check_status === "clear";
  return (
    <Link
      to={`/provider/${p.slug}`}
      className={`bg-white rounded-xl overflow-hidden hover:-translate-y-[2px] hover:shadow-gold-glow transition-all relative ${
        bgCleared
          ? "border-2 border-[#0F8043]/40 hover:border-[#0F8043]/70"
          : "border border-[#EAEAEA] hover:border-gold/55"
      }`}
      data-testid={`provider-card-${p.id}`}
    >
      {bgCleared && (
        <div
          className="absolute top-0 right-0 z-10 bg-[#0F8043] text-white text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-bl-lg flex items-center gap-1"
          data-testid={`card-bg-ribbon-${p.id}`}
        >
          <ShieldCheck size={10} weight="fill" /> Cleared
        </div>
      )}
      <div className="aspect-[16/10] bg-[#FAFAFA] relative">
        {p.photo_url ? (
          <img src={publicFileUrl(p.photo_url)} alt={p.business_name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon size={48} weight="duotone" className="text-[#C4C4C4]" />
          </div>
        )}
        <span
          className="absolute top-3 left-3 text-[9.5px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-white/95 backdrop-blur-sm flex items-center gap-1"
          style={{ color: meta.accent }}
        >
          <Icon size={10} weight="duotone" />
          {p.kind === "field_host" ? "Zoomie Time" : (p.specialty_label || meta.label.replace(/s$/, ""))}
        </span>
        {p.insurance_verified ? (
          <span className={`absolute ${bgCleared ? "top-12" : "top-3"} right-3 text-[9.5px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-[#0F8043]/95 text-white flex items-center gap-1`} data-testid={`card-verified-${p.id}`}>
            <ShieldCheck size={10} weight="fill" /> Verified
          </span>
        ) : p.has_insurance ? (
          <span className={`absolute ${bgCleared ? "top-12" : "top-3"} right-3 text-[9.5px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-white/95 backdrop-blur-sm text-[#0F8043] border border-[#0F8043]/30 flex items-center gap-1`}>
            <ShieldCheck size={10} weight="duotone" /> Insured
          </span>
        ) : null}
      </div>
      <div className="p-4">
        <p className="font-spectral text-lg leading-tight text-ink">{p.business_name}</p>
        {p.kind === "field_host" && p.land && (
          <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`land-badges-${p.id}`}>
            <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-[#EAF4EC] text-[#2E6B3E]">{p.land.acres} acres</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${p.land.fenced === "fully" ? "bg-[#EAF4EC] text-[#2E6B3E]" : "bg-[#FBF3E2] text-[#8A6A00]"}`}>
              {p.land.fenced === "fully" ? "Fully fenced" : p.land.fenced === "partially" ? "Partially fenced" : "Unfenced"}
            </span>
            {p.land.water_access && <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-[#EAF1F7] text-[#3A6EA5]">Water</span>}
            {p.land.agility_equipment && <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-[#EAF1F7] text-[#3A6EA5]">Agility</span>}
          </div>
        )}
        {p.tagline && <p className="text-[12.5px] text-[#6B6B6B] mt-1 line-clamp-2 leading-snug">{p.tagline}</p>}
        {p.rating?.count > 0 && (
          <div className="flex items-center gap-1.5 mt-2 text-[11.5px] text-[#6B6B6B]" data-testid={`card-rating-${p.id}`}>
            <StarRating value={p.rating.avg} size={11} />
            <span className="font-tabular font-semibold text-ink">{p.rating.avg.toFixed(1)}</span>
            <span>· {p.rating.count}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5 mt-3 text-[11.5px] text-[#6B6B6B]">
          <MapPin size={11} weight="duotone" />
          {p.location?.city}, {p.location?.state}
        </div>
        {match?.score != null && (
          <p
            className={`text-[11px] font-bold rounded-full px-2.5 py-1 mt-2 inline-flex items-center gap-1 ${
              match.score >= 75 ? "bg-[#0F8043]/10 text-[#0F8043]" : "bg-cream text-[#8A6A00]"
            }`}
            title={match.reasons?.join(" · ") || ""}
            data-testid={`card-match-${p.id}`}
          >
            ♥ {match.score}% care match for your dogs
          </p>
        )}
        {bgCleared && (
          <p className="text-[10.5px] font-semibold uppercase tracking-wider text-[#0F8043] mt-2 inline-flex items-center gap-1" data-testid={`card-bg-clear-${p.id}`}>
            <ShieldCheck size={10} weight="fill" /> Background check cleared
          </p>
        )}
      </div>
    </Link>
  );
}
